import { type ReactNode, useMemo } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import type { ApolloError } from '@apollo/client';
import type { MediaStateIdEnum } from '@frameio/graphql-client/generated';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '@frameio/utils';
import type { MediaDataType } from '../transfers/types';
import { useActiveMediaStateIndex } from './ComparisonAssetsContext/ComparisonAssetsContext';

const MediaViewerDataContext = createContext<{
  mediaDataList: MediaDataType[] | null;
  error?: ApolloError | unknown;
  mediaStateId: MediaStateIdEnum;
}>({
  mediaDataList: EMPTY_ARRAY,
  mediaStateId: 'MEDIA_STATE_DEFAULT',
});

export const MediaViewerDataContextProvider = ({
  mediaStateId,
  mediaDataList,
  error,
  children,
}: {
  mediaDataList: MediaDataType[] | null;
  children: ReactNode;
  error?: ApolloError | unknown;
  mediaStateId: MediaStateIdEnum;
}) => {
  const value = useMemo(
    () => ({
      mediaDataList,
      error,
      mediaStateId,
    }),
    [error, mediaDataList, mediaStateId]
  );

  return (
    <MediaViewerDataContext.Provider value={value}>
      {children}
    </MediaViewerDataContext.Provider>
  );
};

export const useMediaData = (index?: number) => {
  const activeIndex = useActiveMediaStateIndex();
  return useContextSelector(
    MediaViewerDataContext,
    ({ mediaDataList }) =>
      mediaDataList?.[index ?? activeIndex] || (EMPTY_OBJECT as MediaDataType)
  );
};

export const useMediaDataList = () =>
  useContextSelector(
    MediaViewerDataContext,
    ({ mediaDataList }) => mediaDataList || (EMPTY_ARRAY as MediaDataType[])
  );

export const useMediaError = () =>
  useContextSelector(MediaViewerDataContext, ({ error }) => error);

export const useMediaTypename = (index?: number) => {
  return useMediaData(index)?.__typename;
};

export const useMediaIsWatermarked = (index?: number) => {
  return useMediaData(index)?.isWatermarked;
};

export const useMediaEncodeStatus = (index?: number) => {
  const mediaData = useMediaData(index);
  if (mediaData.__typename === 'DocumentMedia') {
    return mediaData?.pdfProxyTranscode?.encodeStatus;
  }
  return null;
};

export const useMediaStateId = () =>
  useContextSelector(
    MediaViewerDataContext,
    ({ mediaStateId }) => mediaStateId
  );
